<script setup lang="ts">
import { computed, watchEffect } from "vue";

import type { ChartSpec } from "../sbi/protocol";
import LucentIcon from "./LucentIcon.vue";
import LucentSpinner from "./LucentSpinner.vue";
import { formatError } from "./errorText.ts";
import { resolveChartHeight } from "./chart-heights";

import InsightAreaChart from "./InsightAreaChart.vue";
import InsightBarChart from "./InsightBarChart.vue";
import InsightBoxplotChart from "./InsightBoxplotChart.vue";
import InsightCalendarChart from "./InsightCalendarChart.vue";
import InsightCandlestickChart from "./InsightCandlestickChart.vue";
import InsightDonutChart from "./InsightDonutChart.vue";
import InsightFunnelChart from "./InsightFunnelChart.vue";
import InsightGaugeChart from "./InsightGaugeChart.vue";
import InsightHeatmap from "./InsightHeatmap.vue";
import InsightHistogram from "./InsightHistogram.vue";
import InsightLineChart from "./InsightLineChart.vue";
import InsightMixedChart from "./InsightMixedChart.vue";
import InsightPieChart from "./InsightPieChart.vue";
import InsightPolarBarChart from "./InsightPolarBarChart.vue";
import InsightPunchcardChart from "./InsightPunchcardChart.vue";
import InsightRadarChart from "./InsightRadarChart.vue";
import InsightSankeyChart from "./InsightSankeyChart.vue";
import InsightScatterChart from "./InsightScatterChart.vue";
import InsightStackedBarChart from "./InsightStackedBarChart.vue";
import InsightSunburstChart from "./InsightSunburstChart.vue";
import InsightTreeChart from "./InsightTreeChart.vue";
import InsightWaterfallChart from "./InsightWaterfallChart.vue";

const props = withDefaults(
  defineProps<{
    spec: ChartSpec;
    loading?: boolean;
    /**
     * Set when the marker bound to this chart's `data` slot rejected,
     * or when the resolved row count exceeded the per-type cap.
     * Renders `spec.error` (or the generic fallback) in place of the
     * chart body. Mutually exclusive with `loading`.
     */
    errored?: boolean;
    /**
     * 8-code lucent-error taxonomy when `errored` is set. Forwarded
     * up through the host as a DOM `lucent-error` CustomEvent.
     */
    errorCode?: string;
    /**
     * Error correlation tag minted at the failure site (server or
     * worker). Appended to the user-facing error banner as
     * `[EX:<id>]`. See `errorText.ts` / `docs/plans/error-correlation-ids.md`.
     */
    exId?: string;
    /**
     * Field → format registry from `sbi.format(...)`. Forwarded to
     * each chart subcomponent so axis tick formatters can fall back
     * to a registered format for `spec.x` / `spec.y` when the spec
     * doesn't carry an explicit `xFormat` / `yFormat`.
     */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emitChart = defineEmits<{
  (e: "click-target", id: string, datum?: Record<string, unknown>): void;
  /**
   * Non-fatal warnings from per-chart components — sankey cycle
   * detection, sunburst depth-cap truncation, etc. Surfaced through
   * `<lucent-insight>` as `lucent-diagnostic` DOM events for
   * embedders to feed into observability sinks. Coalesced by code
   * per-render-pass at the EmbedHost level.
   */
  (e: "diagnostic", code: string, detail: Record<string, unknown>): void;
  /**
   * Per-chart error with code from the 8-code lucent-error taxonomy.
   * EmbedHost forwards as a Vue `error` event, which
   * defineCustomElement translates to a DOM `lucent-error`.
   */
  (
    e: "chart-error",
    payload: {
      code: string;
      message: string;
      chartType: string;
      targetId?: string;
    },
  ): void;
}>();

watchEffect(() => {
  if (props.errored && props.errorCode) {
    emitChart("chart-error", {
      code: props.errorCode,
      message:
        (props.spec as { error?: string }).error ?? "Chart failed to render",
      chartType: (props.spec as { type?: string }).type ?? "",
      targetId: (props.spec as { id?: string }).id,
    });
  }
});

// Match the chart's intended height — the same per-type default and
// clamp the chart itself uses — so the placeholder doesn't shift layout.
const placeholderHeight = computed(
  () => `${resolveChartHeight(props.spec.type, props.spec.height)}px`,
);

// A chart counts as "empty" when the marker resolved cleanly but the
// data array is empty. Authors opt in to a per-chart placeholder by
// setting `spec.empty`; otherwise the chart renders its frame as
// usual (axes with no series data, which is the historical
// behaviour).
//
// `gauge` carries its marker on `spec.value` (single-scalar chart),
// not `spec.data` — so we look at `value` for gauge and `data` for
// every other type. Without this branch, every successfully resolved
// gauge looks "empty" to the dispatcher and gets replaced with the
// empty placeholder.
const dataRows = computed(() => {
  const spec = props.spec as { type?: string; data?: unknown; value?: unknown };
  if (spec.type === "gauge") {
    const v = spec.value;
    if (typeof v === "number") return Number.isFinite(v) ? [v] : [];
    if (Array.isArray(v)) return v;
    return [];
  }
  return Array.isArray(spec.data) ? spec.data : [];
});
const isEmpty = computed(
  () => !props.loading && !props.errored && dataRows.value.length === 0,
);

const emptyText = computed(() => (props.spec as { empty?: string }).empty);
const errorText = computed(() =>
  formatError(
    (props.spec as { error?: string }).error ?? "Couldn't load this chart",
    props.exId,
  ),
);
</script>

<template>
  <!--
    Single root wrapper. The header and chart need to read as one card
    with no gap between them — but every parent layout (EmbedHost
    `.chrome-tab-body`, `InsightColumns`, `InsightSection`, …) is a
    flex column with a gap, so a multi-root template would force that
    parent gap between the title and the chart body.

    Wrapping in `.chart-shell` collapses the two into one flex child
    of the parent. The wrapper sets `display: flex; width: 100%;
    min-width: 0` so the chart subcomponent's `width: 100%` still
    resolves correctly — ECharts' `autoresize` measures the chart
    element directly, the same way it did before when no wrapper was
    present.

    The `.chart-title + .chart` rule in the stylesheet stitches the
    two children together visually (header keeps top corners, chart
    keeps bottom corners, single divider line at the seam).
  -->
  <div class="chart-shell">
    <header v-if="spec.title" class="chart-title">
      <!--
        The default `◆` leading mark is the chart's identifier glyph.
        When the author opts in to `titleIcon` with the default
        `leading` position, the icon REPLACES the mark (rather than
        sitting next to it) — otherwise authors who reach for the
        feature get an unwanted double-glyph stack. Trailing-position
        icons leave the mark in place; they're additive on the right
        side of the title.
      -->
      <LucentIcon
        v-if="spec.titleIcon && (spec.titleIconPosition ?? 'leading') === 'leading'"
        :name="spec.titleIcon"
        :size="14"
        class="chart-title-icon chart-title-icon--leading"
      />
      <span
        v-else
        class="chart-title-mark"
        aria-hidden="true"
      >◆</span>
      <span class="chart-title-text">{{ spec.title }}</span>
      <LucentIcon
        v-if="spec.titleIcon && spec.titleIconPosition === 'trailing'"
        :name="spec.titleIcon"
        :size="14"
        class="chart-title-icon chart-title-icon--trailing"
      />
    </header>
    <div
      v-if="loading"
      class="chart chart-loading"
      :style="{ height: placeholderHeight }"
    >
      <LucentSpinner :size="48" />
    </div>
    <div
      v-else-if="errored"
      class="chart chart-placeholder chart-placeholder--error"
      :style="{ height: placeholderHeight }"
    >
      {{ errorText }}
    </div>
    <div
      v-else-if="isEmpty && emptyText"
      class="chart chart-placeholder chart-placeholder--empty"
      :style="{ height: placeholderHeight }"
    >
      {{ emptyText }}
    </div>
    <InsightLineChart
    v-else-if="spec.type === 'line'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightBarChart
    v-else-if="spec.type === 'bar'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightStackedBarChart
    v-else-if="spec.type === 'stacked_bar'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightScatterChart
    v-else-if="spec.type === 'scatter'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightHistogram
    v-else-if="spec.type === 'histogram'"
    :spec="spec"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightAreaChart
    v-else-if="spec.type === 'area'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightHeatmap
    v-else-if="spec.type === 'heatmap'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightBoxplotChart
    v-else-if="spec.type === 'boxplot'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightRadarChart
    v-else-if="spec.type === 'radar'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightWaterfallChart
    v-else-if="spec.type === 'waterfall'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightGaugeChart
    v-else-if="spec.type === 'gauge'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightMixedChart
    v-else-if="spec.type === 'mixed'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightPolarBarChart
    v-else-if="spec.type === 'polar_bar'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightDonutChart
    v-else-if="spec.type === 'donut'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightPieChart
    v-else-if="spec.type === 'pie'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightPunchcardChart
    v-else-if="spec.type === 'punchcard'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightCandlestickChart
    v-else-if="spec.type === 'candlestick'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightSunburstChart
    v-else-if="spec.type === 'sunburst'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
    @diagnostic="(code, detail) => $emit('diagnostic', code, detail)"
  />
  <InsightTreeChart
    v-else-if="spec.type === 'tree'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
    @diagnostic="(code, detail) => $emit('diagnostic', code, detail)"
    @chart-error="(payload) => $emit('chart-error', payload)"
  />
  <InsightSankeyChart
    v-else-if="spec.type === 'sankey'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
    @diagnostic="(code, detail) => $emit('diagnostic', code, detail)"
  />
  <InsightFunnelChart
    v-else-if="spec.type === 'funnel'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  <InsightCalendarChart
    v-else-if="spec.type === 'calendar'"
    :spec="spec"
    :formats="formats"
    @click-target="(id, datum) => $emit('click-target', id, datum)"
  />
  </div>
</template>

<style scoped>
/* Outer wrapper. Collapses the title + chart into one flex child of
 * the parent layout, so the parent's row-gap doesn't open a gap
 * between the title bar and the chart body. The flex column +
 * `width: 100%; min-width: 0` preserves the width signal ECharts'
 * `autoresize` needs — the chart subcomponent's `width: 100%` still
 * resolves against this wrapper, which fills the parent cell. */
.chart-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
}

/* Card chrome painted directly onto every chart root — both the real
 * chart (`<v-chart class="chart">` rendered by the subcomponents) and
 * the loading / placeholder divs above. Vue 3 stamps this component's
 * scoped `data-v-*` onto each branch inside the wrapper, including
 * the subcomponent root, so a bare `.chart` selector reaches the
 * chart element without `:deep`. */
.chart {
  /* `display: block` and `width: 100%` would normally come from
   * vue-echarts' own global stylesheet (`x-vue-echarts { display:
   * block; width: 100%; ... }`) — but that rule is injected into
   * `document.head` and never reaches the shadow root that hosts
   * `<lucent-insight>`. Without an explicit declaration here, the
   * `<x-vue-echarts>` custom element falls back to its default
   * `display: inline`, `width: 100%` has no effect, and ECharts
   * measures `offsetWidth === 0` then falls back to its internal
   * 100px default — every chart renders 100px wide regardless of
   * available space. */
  display: block;
  width: 100%;
  min-width: 0;
  background: var(--card);
  /* Themer-facing: --lucent-card-border / --lucent-card-shadow. */
  border: var(--lucent-card-border, 1px solid var(--rule));
  border-radius: 10px;
  box-shadow: var(--lucent-card-shadow, none);
  box-sizing: border-box;
}

/* Title bar above the chart. Shares the dashboard's mono / uppercase
 * eyebrow treatment (see `.ilt-eyebrow`, KPI `.label`, table
 * `thead th`) so chart titles read as part of the same family. The
 * `--rule-soft` strip and the diamond mark mirror the workspace
 * eyebrow on the insights list page. */
.chart-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--rule-soft);
  border: 1px solid var(--rule);
  border-radius: 10px 10px 0 0;
  font-family: var(--mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--card-muted);
  line-height: 1.2;
  box-sizing: border-box;
  /* Fixed header cell height — single-line titles all render at the
   * same size so charts line up visually. When the title text wraps
   * (long titles, narrow columns), the cell grows past min-height to
   * fit the wrapped lines. */
  min-height: 36px;
}
.chart-title-mark {
  color: var(--primary-deep);
  font-size: 11px;
  line-height: 1;
}
:global(html[data-theme="dark"]) .chart-title-mark {
  color: var(--primary);
}
.chart-title-text {
  color: var(--card-ink);
  letter-spacing: 0.14em;
}
.chart-title-icon {
  flex: 0 0 auto;
  color: var(--card-muted);
  align-self: center;
}
.chart-title-icon--leading {
  margin-right: 4px;
}
.chart-title-icon--trailing {
  margin-left: 6px;
}

/* When a title precedes the chart, stitch the two boxes into one
 * card: the header keeps the top rounded corners, the chart loses
 * its top border (the header's bottom border becomes the divider)
 * and squares its top corners so the seam is invisible. */
.chart-title + .chart {
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

/* Placeholders (loading / empty / error) reuse the same card chrome
 * via the shared `.chart` rule above; the rules below add the
 * centred-content layout and text styling those states need. */
.chart-loading,
.chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.chart-placeholder {
  text-align: center;
  font-size: 13px;
  line-height: 1.4;
  color: var(--card-muted);
}
.chart-placeholder--error {
  color: var(--color-negative);
  background: rgba(188, 18, 0, 0.04);
}
</style>
